import React from "react";

import { IconButton } from "../design/Button";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const FIELD =
  "[autofocus], .modal-body input:not([disabled]), .modal-body textarea:not([disabled]), .modal-body [role=combobox]";

/** Marks everything outside ``el`` inert so screen readers stay in the
 * dialog; returns the undo. */
function inertOutside(el: HTMLElement): () => void {
  const done: HTMLElement[] = [];
  for (let n: HTMLElement | null = el; n && n !== document.body; ) {
    const parent: HTMLElement | null = n.parentElement;
    for (const sib of parent?.children ?? []) {
      if (sib !== n && sib instanceof HTMLElement && !sib.inert) {
        sib.inert = true;
        done.push(sib);
      }
    }
    n = parent;
  }
  return () => done.forEach((d) => (d.inert = false));
}

/** Moves focus into ``ref`` on mount, keeps Tab inside it, closes on Escape
 * and hands focus back to whatever had it before. */
export function useFocusTrap(
  ref: React.RefObject<HTMLElement>,
  onClose: () => void,
) {
  const close = React.useRef(onClose);
  close.current = onClose;
  React.useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const el = ref.current;
    (
      el?.querySelector<HTMLElement>(FIELD) ??
      el?.querySelector<HTMLElement>(FOCUSABLE)
    )?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        close.current();
        return;
      }
      if (e.key !== "Tab" || !el) return;
      const items = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!el.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      before?.focus?.();
    };
  }, [ref]);
}

export function Modal({
  eyebrow,
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  eyebrow?: string;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}) {
  const card = React.useRef<HTMLDivElement>(null);
  const scrim = React.useRef<HTMLDivElement>(null);
  // Runs before the trap's effect: grab the opener before inert blurs it, and
  // on close lift inert first so focus can go back to it.
  React.useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const undo = scrim.current ? inertOutside(scrim.current) : undefined;
    return () => {
      undo?.();
      opener?.focus?.();
    };
  }, []);
  useFocusTrap(card, onClose);

  return (
    <div ref={scrim} className="modal-scrim" onClick={onClose}>
      <div
        ref={card}
        className={`modal-card fade-in${wide ? " wide" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="modal-head">
          <div>
            {eyebrow && <div className="eyebrow">{eyebrow}</div>}
            <h3 className="modal-title">{title}</h3>
          </div>
          <IconButton icon="x" label="Close" onClick={onClose} />
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}
